import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ClipboardList, Database, EyeOff, KeyRound, Lock, Server, ShieldCheck, Users } from "lucide-react";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["นโยบายข้อมูล", "/privacy-policy"],
  ["ติดต่อ", "/contact"],
];

const AppNav = () => (
  <header className="sticky top-0 z-50 w-full border-b border-[#E3E6DF] bg-[#FAFAF7]/90 backdrop-blur-xl">
    <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img src="/logo_and_name.png" alt="KHORN AI" className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none" />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 lg:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="text-xs font-bold uppercase tracking-[0.16em] text-[#6A7168] transition hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <div className="flex items-center gap-3">
        <a href="https://app.khorn-ai.com" className="hidden text-xs font-bold uppercase tracking-[0.16em] text-[#6A7168] transition hover:text-[#15130F] md:block">
          เข้าสู่ระบบ
        </a>
        <a href="https://app.khorn-ai.com/plan" className="rounded-full bg-[#15130F] px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#1F2A22]">
          เริ่มใช้งาน
        </a>
      </div>
    </div>
  </header>
);

const principles = [
  {
    icon: EyeOff,
    title: "ไม่ใช้ข้อมูลผู้ใช้ไปฝึกโมเดล",
    desc: "ข้อมูลสัญญา เอกสารคดี ข้อเท็จจริง และคำถามของผู้ใช้จะไม่ถูกนำไปฝึกโมเดลสาธารณะ",
  },
  {
    icon: Lock,
    title: "การเข้ารหัส",
    desc: "ข้อมูลถูกเข้ารหัสระหว่างรับส่งและระหว่างจัดเก็บ เพื่อรองรับงานกฎหมายที่มีความละเอียดอ่อน",
  },
  {
    icon: Users,
    title: "ควบคุมสิทธิ์ตามบทบาท",
    desc: "ควบคุมการเข้าถึงตามบทบาทของทีม สำนักงาน และเคสที่เกี่ยวข้อง",
  },
  {
    icon: ClipboardList,
    title: "ประวัติการทำงาน",
    desc: "เก็บประวัติการแก้ไข การมอบหมายงาน การตรวจ และการอนุมัติในขั้นตอนงาน",
  },
];

const dataItems = [
  ["ข้อมูลบัญชี", "ใช้สำหรับยืนยันตัวตน การเข้าสู่ระบบ การออกใบเสร็จ และการดูแลความปลอดภัยของบัญชี"],
  ["เอกสารและข้อมูลเคส", "ใช้เพื่อสรุป จัดหลักฐาน ร่างเอกสาร หาฐานกฎหมาย และติดตามงานค้างให้ผู้ใช้เท่านั้น"],
  ["ประวัติการใช้งาน", "ใช้เพื่อให้ทีมกลับมาตรวจงานเดิม ดูสถานะงาน และติดตามการเปลี่ยนแปลงย้อนหลัง"],
  ["Google API", "เข้าถึงเฉพาะเมื่อผู้ใช้อนุญาตผ่าน OAuth และใช้ตามนโยบายข้อมูลผู้ใช้ของ Google API"],
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#15130F] selection:bg-[#15130F] selection:text-white">
      <Helmet>
        <title>ความปลอดภัย KHORN AI | Privacy สำหรับสำนักงานกฎหมาย</title>
        <meta
          name="description"
          content="นโยบายความปลอดภัยของ KHORN AI สำหรับข้อมูลลูกความ เอกสารคดี และงานกฎหมาย พร้อม no-training policy การเข้ารหัส role-based access และ audit trail"
        />
        <meta name="keywords" content="KHORN AI privacy, ความปลอดภัยข้อมูลกฎหมาย, no training AI, PDPA legal AI, encrypted legal documents" />
        <meta property="og:title" content="ความปลอดภัย KHORN AI - Privacy สำหรับสำนักงานกฎหมาย" />
        <meta property="og:description" content="No-training policy, encryption, role-based access และ audit trail สำหรับงานลูกความและเอกสารกฎหมาย" />
        <meta property="og:url" content="https://www.khorn-ai.com/privacy" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.khorn-ai.com/privacy" />
      </Helmet>

      <AppNav />

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute left-1/2 top-0 h-[520px] w-[860px] -translate-x-1/2 rounded-full bg-[#EEF3EA]/70 blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:py-28">
            <div>
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#E3E6DF] bg-[#FFFFFF] px-4 py-2 text-xs font-bold text-[#6A7168] shadow-sm">
                <ShieldCheck size={14} />
                Security and privacy
              </div>
              <h1 className="text-5xl font-semibold leading-tight tracking-tight md:text-7xl">
                ความปลอดภัยสำหรับข้อมูลลูกความและเอกสารคดี
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[#5F665E]">
                KHORN AI ถูกออกแบบให้ใช้งานกับสำนักงานกฎหมายที่ต้องควบคุมข้อมูลละเอียดอ่อน สิทธิ์ของทีม และประวัติการทำงานในแต่ละเคส
              </p>
            </div>

            <div className="rounded-[32px] border border-[#E3E6DF] bg-[#FFFFFF] p-5 shadow-[0_30px_100px_rgba(0,0,0,0.10)]">
              <div className="rounded-[26px] bg-[#FFFFFF] p-7">
                <div className="mb-8 flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#15130F] text-white">
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">พื้นที่ทำงานกฎหมายแบบส่วนตัว</div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6A7168]">ไม่ใช้ข้อมูลไปฝึกโมเดลสาธารณะ</div>
                  </div>
                </div>
                <div className="space-y-3">
                  {["ข้อมูลเข้ารหัส", "ประมวลผลเอกสารแบบส่วนตัว", "ควบคุมสิทธิ์ตามบทบาท", "มีประวัติการทำงานย้อนหลัง"].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-2xl border border-[#E3E6DF] bg-[#FFFFFF] px-4 py-3 text-sm text-[#5F665E]">
                      <span className="h-2 w-2 rounded-full bg-[#8FA083]" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-[#E3E6DF] bg-[#FFFFFF] py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <div className="mb-14 max-w-4xl">
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-[#6A7168]">หลักความปลอดภัย</div>
              <h2 className="text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                ควบคุมข้อมูล ทีม และประวัติการทำงานของแต่ละเคส
              </h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {principles.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="rounded-[28px] border border-[#E3E6DF] bg-[#FFFFFF] p-7 shadow-sm">
                    <Icon size={24} className="mb-8 text-[#6A7168]" />
                    <h3 className="text-xl font-semibold">{item.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-[#5F665E]">{item.desc}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <div className="rounded-[32px] border border-[#E3E6DF] bg-[#FFFFFF] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.08)] md:p-10">
              <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                <div>
                  <div className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-[#6A7168]">การใช้ข้อมูล</div>
                  <h2 className="max-w-3xl text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                    เราใช้ข้อมูลเพื่อช่วยขับเคลื่อนงานกฎหมายของคุณเท่านั้น
                  </h2>
                </div>
                <Server size={52} className="text-[#6A7168]" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {dataItems.map(([title, desc]) => (
                  <div key={title} className="rounded-2xl border border-[#E3E6DF] bg-[#FFFFFF] p-6">
                    <div className="mb-3 text-sm font-semibold">{title}</div>
                    <p className="text-sm leading-relaxed text-[#5F665E]">{desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-4 border-t border-[#E3E6DF] pt-8 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3 text-sm text-[#5F665E]">
                  <KeyRound size={18} className="text-[#6A7168]" />
                  ผู้ใช้สามารถเพิกถอนสิทธิ์ Google API ได้ทุกเมื่อผ่านบัญชี Google
                </div>
                <Link to="/privacy-policy" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#15130F] px-6 text-sm font-bold text-white transition hover:bg-[#1F2A22]">
                  อ่านนโยบายฉบับเต็ม
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-[#E3E6DF] bg-[#15130F] py-16 text-white">
          <div className="mx-auto grid max-w-7xl gap-6 px-5 md:grid-cols-3 md:px-8">
            {[
              [Database, "User-owned data", "ข้อมูลสำนักงานและข้อมูลลูกความยังเป็นของผู้ใช้"],
              [Lock, "ขั้นตอนงานปลอดภัย", "ออกแบบให้ใช้กับเอกสารและงานกฎหมายที่ละเอียดอ่อน"],
              [ShieldCheck, "ไม่ฝึกโมเดลสาธารณะ", "ไม่ใช้ข้อมูลของผู้ใช้ไปฝึกโมเดลสาธารณะ"],
            ].map(([Icon, title, desc]) => (
              <article key={title} className="rounded-[26px] border border-white/10 bg-white/[0.08] p-7">
                <Icon size={24} className="mb-8 text-[#73d99f]" />
                <h3 className="text-2xl font-semibold">{title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/58">{desc}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
